import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFormType } from '@neo/blocks/forms/types'
import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { Popover, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import { Slider } from '@neo/primitives/slider'
import { Switch } from '@neo/primitives/switch'
import { Tooltip } from '@neo/primitives/tooltip'
import { Typography } from '@neo/primitives/typography'
import { Info, Settings } from 'lucide-react'
import React from 'react'
import { backgroundAudioSchema } from '../../config/agent-settings.validation.config'
import { BACKGROUND_AUDIO_OPTIONS, BackgroundAudioType } from '../../config/background-audio.config'
import type { AgentSettingsFormValues } from '../../config/base.config'

const VolumeSlider = ({ form }: { form: GenericFormType<AgentSettingsFormValues> }) => {
  const volume = form.useStore(
    (state) => state.values.voice_ai_config?.background_audio?.volume ?? 1,
  )

  const handleVolumeChange = (value: number[]) => {
    form.setFieldValue('voice_ai_config.background_audio.volume', value[0])
  }

  return (
    <div className="flex flex-col gap-2 w-64">
      <Typography variant="P/500" className="text-primary">
        Background audio volume
      </Typography>
      <div className="flex items-center gap-3">
        <Slider
          value={[volume]}
          onValueChange={handleVolumeChange}
          min={0}
          max={2}
          step={0.01}
          data-testid="slider-background-audio-volume"
        />
        <span className="text-p-500 font-jetbrains tabular-nums text-primary w-10 text-right">
          {volume.toFixed(2)}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-s-400 text-secondary">Min</span>
        <span className="text-s-400 text-secondary">Max</span>
      </div>
    </div>
  )
}

const ThinkingSoundVolumeSlider = ({
  form,
}: {
  form: GenericFormType<AgentSettingsFormValues>
}) => {
  const volume = form.useStore(
    (state) => state.values.voice_ai_config?.background_audio?.thinking_sound_volume ?? 0.7,
  )

  const handleVolumeChange = (value: number[]) => {
    form.setFieldValue('voice_ai_config.background_audio.thinking_sound_volume', value[0])
  }

  return (
    <div className="flex flex-col gap-2 w-64">
      <Typography variant="P/500" className="text-primary">
        Thinking sound volume
      </Typography>
      <div className="flex items-center gap-3">
        <Slider
          value={[volume]}
          onValueChange={handleVolumeChange}
          min={0}
          max={1}
          step={0.01}
          data-testid="slider-thinking-sound-volume"
        />
        <span className="text-p-500 font-jetbrains tabular-nums text-primary w-10 text-right">
          {volume.toFixed(2)}
        </span>
      </div>
      <div className="flex justify-between">
        <span className="text-s-400 text-secondary">Min</span>
        <span className="text-s-400 text-secondary">Max</span>
      </div>
    </div>
  )
}

export const BackgroundAudioConfig = React.memo(
  ({ form }: { form: GenericFormType<AgentSettingsFormValues> }) => {
    const audioType = form.useStore((state) => state.values.voice_ai_config?.background_audio?.type)
    const isAudioTypeSelected = audioType && audioType !== BackgroundAudioType.NONE
    const thinkingSoundEnabled = form.useStore(
      (state) => state.values.voice_ai_config?.background_audio?.thinking_sound_enabled ?? true,
    )

    const handleThinkingToggle = (checked: boolean) => {
      form.setFieldValue('voice_ai_config.background_audio.thinking_sound_enabled', checked)
    }

    return (
      <div className="grid gap-4 rounded-md border border-border bg-bg2 p-4">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <FieldsRenderer
              fields={[
                {
                  name: 'voice_ai_config.background_audio.type',
                  componentType: 'select',
                  label: (
                    <div className="mb-1 flex items-center gap-1 text-p-500 text-foreground">
                      Background audio
                      <Tooltip content="Play subtle ambient audio while the agent speaks">
                        <Info className="size-3 text-tertiary" />
                      </Tooltip>
                    </div>
                  ),
                  zodValidation: backgroundAudioSchema.shape.type,
                  inputProps: {
                    placeholder: 'Select background audio',
                    options: BACKGROUND_AUDIO_OPTIONS.map((opt) => ({
                      label: opt.label,
                      value: opt.value,
                    })),
                  },
                },
              ]}
              form={form}
            />
          </div>
          {isAudioTypeSelected && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  data-testid="button-background-audio-settings"
                  className="mt-5 size-9 rounded-md border border-border bg-background hover:bg-hv1"
                >
                  <Settings className="size-4 text-secondary" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-auto border border-border bg-popover shadow-bs1"
              >
                <VolumeSlider form={form} />
              </PopoverContent>
            </Popover>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border pt-4">
          <div className="flex items-center gap-1 text-p-500 text-foreground">
            Thinking sounds
            <Tooltip content="Play subtle keyboard-typing sounds while the agent is processing">
              <Info className="size-3 text-tertiary" />
            </Tooltip>
          </div>
          <div className="flex items-center gap-1">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  data-testid="button-thinking-sound-settings"
                  className={cn(
                    'size-9 rounded-md border border-border bg-background hover:bg-hv1',
                    thinkingSoundEnabled ? '' : 'invisible pointer-events-none',
                  )}
                  aria-hidden={!thinkingSoundEnabled}
                  tabIndex={thinkingSoundEnabled ? 0 : -1}
                >
                  <Settings className="size-4 text-secondary" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-auto border border-border bg-popover shadow-bs1"
              >
                <ThinkingSoundVolumeSlider form={form} />
              </PopoverContent>
            </Popover>
            <Switch
              value={thinkingSoundEnabled}
              onChange={handleThinkingToggle}
              data-testid="switch-thinking-sound-enabled"
            />
          </div>
        </div>
      </div>
    )
  },
)
